import {
  CapaPurchaseOrderStatus,
  CapaQuotationChatRoomStatus,
  useFetchPurchaseOrdersQuery,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import { useSelector } from '@store';

interface UsePurchaseOrderProps {
  rfqId?: string;
  quotationPartnerId?: string;
}

const getCurrentStepIndex = (status?: CapaPurchaseOrderStatus) => {
  switch (status) {
    case CapaPurchaseOrderStatus.Created:
    case CapaPurchaseOrderStatus.ProcessPayment:
    case CapaPurchaseOrderStatus.Paid:
    case CapaPurchaseOrderStatus.NotPaidCanceled:
    case CapaPurchaseOrderStatus.CancelRequested:
    case CapaPurchaseOrderStatus.CancelConfirmed:
    case CapaPurchaseOrderStatus.RejectOrderByPartner:
      return 0;

    case CapaPurchaseOrderStatus.Processing:
    case CapaPurchaseOrderStatus.RefundRequested:
    case CapaPurchaseOrderStatus.RefundConfirmed:
    case CapaPurchaseOrderStatus.CancelledByAdmin:
      return 1;

    case CapaPurchaseOrderStatus.Shipping:
      return 2;

    case CapaPurchaseOrderStatus.PurchaseConfirmed:
      return 3;

    default:
      return -1;
  }
};

const usePurchaseOrder = ({
  rfqId = '',
  quotationPartnerId = ''
}: UsePurchaseOrderProps) => {
  const { data: fetchPurchaseOrders } = useFetchPurchaseOrdersQuery({
    variables: {
      input: {
        rfqId,
        quotationPartnerId
      }
    },
    skip: !rfqId || !quotationPartnerId,
    fetchPolicy: 'cache-and-network'
  });

  const { data: fetchRfqDetail } = useFetchQuotationUserRfqDetailQuery({
    variables: { rfqId },
    skip: !rfqId,
    fetchPolicy: 'cache-first'
  });

  const selectedPurchaseOrderId = useSelector(
    ({ documents }) => documents?.selectedPurchaseOrderId
  );

  const purchaseOrders = fetchPurchaseOrders?.fetchPurchaseOrders.data ?? [];
  const hasPurchaseOrder = purchaseOrders.length > 0;
  const paymentDataList = purchaseOrders?.[0]?.paymentDataList ?? [];
  const selectedPurchaseOrder = hasPurchaseOrder
    ? selectedPurchaseOrderId
      ? purchaseOrders.find((order) => order.id === selectedPurchaseOrderId)
      : purchaseOrders?.[0]
    : undefined;
  const rfqDetail =
    fetchRfqDetail?.fetchQuotationUserRfqDetail.data ?? undefined;
  const chatRoomId = selectedPurchaseOrder?.chatRoomId;
  const chatRoom = rfqDetail?.chatRoom?.find(
    (room) => room.chatRoomId === chatRoomId
  );
  const isClosedConsulting = CLOSED_CHAT_ROOM_STATES.includes(
    chatRoom?.chatRoomStatus
  );

  const projectName = rfqDetail?.projectName ?? '';
  const serviceType = rfqDetail?.serviceType ?? undefined;

  const isStoppedPurchaseOrder = PURCAHSE_ORDER_STOPPED_STATES.includes(
    selectedPurchaseOrder?.status
  );
  const currentStepIndex = getCurrentStepIndex(selectedPurchaseOrder?.status);

  return {
    isClosedConsulting,
    isStoppedPurchaseOrder,
    hasPurchaseOrder,
    purchaseOrders,
    selectedPurchaseOrder,
    selectedPurchaseOrderId,
    projectName,
    serviceType,
    paymentDataList,
    currentStepIndex,
    shipping: selectedPurchaseOrder?.shippingDataList ?? undefined
  };
};

export default usePurchaseOrder;

const PURCAHSE_ORDER_STOPPED_STATES: (CapaPurchaseOrderStatus | undefined)[] = [
  CapaPurchaseOrderStatus.CancelRequested,
  CapaPurchaseOrderStatus.CancelConfirmed,
  CapaPurchaseOrderStatus.CancelledByAdmin,
  CapaPurchaseOrderStatus.NotPaidCanceled,
  CapaPurchaseOrderStatus.RefundRequested,
  CapaPurchaseOrderStatus.RefundConfirmed,
  CapaPurchaseOrderStatus.RejectOrderByPartner
];

const CLOSED_CHAT_ROOM_STATES: (
  | CapaQuotationChatRoomStatus
  | undefined
  | null
)[] = [
  CapaQuotationChatRoomStatus.ClosedBeforeConsulting,
  CapaQuotationChatRoomStatus.ClosedClient,
  CapaQuotationChatRoomStatus.ClosedConfirm,
  CapaQuotationChatRoomStatus.ClosedPartner
];
